import { useMemo, useState } from 'react';
import { useOrganizations } from '@/domains/accounts/services/accounts';
import { useAddTerminalToOrganization } from '@/domains/terminals/services/user-terminals';
import { UserTerminal } from '@/shared/types/userTerminalTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import { Loader2, Search, User, SatelliteDish } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';

interface TransferTerminalsToAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  terminals: UserTerminal[];
  selectedRegionCode: string;
}

function terminalMatchesSearch(terminal: UserTerminal, search: string): boolean {
  if (!search.trim()) return true;
  const q = search.toLowerCase().trim();
  return (
    terminal.userTerminalId?.toLowerCase().includes(q) ||
    terminal.kitSerialNumber?.toLowerCase().includes(q) ||
    terminal.dishSerialNumber?.toLowerCase().includes(q) ||
    terminal.organization?.name?.toLowerCase().includes(q) ||
    terminal.organization?.organizationId?.toLowerCase().includes(q) ||
    terminal.serviceLine?.serviceLineName?.toLowerCase().includes(q) ||
    terminal.serviceLine?.serviceLineNumber?.toLowerCase().includes(q)
  );
}

export default function TransferTerminalsToAccountDialog({
  open,
  onOpenChange,
  onSuccess,
  terminals,
  selectedRegionCode,
}: TransferTerminalsToAccountDialogProps) {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [terminalSearchTerm, setTerminalSearchTerm] = useState('');
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations(open, selectedRegionCode);
  const addTerminalToOrganization = useAddTerminalToOrganization();
  const [selectedTerminals, setSelectedTerminals] = useState<UserTerminal[]>([]);

  const terminalsList = useMemo(() => terminals ?? [], [terminals]);

  // Filter terminals by search
  const filteredTerminals = useMemo(() => {
    return terminalsList.filter(t => terminalMatchesSearch(t, terminalSearchTerm)) || [];
  }, [terminalsList, terminalSearchTerm]);

  // Group filtered terminals by account (name for display)
  const groupedTerminals = useMemo(() => {
    const grouped: Record<string, UserTerminal[]> = {};
    filteredTerminals.forEach(terminal => {
      const accountKey = terminal.organization?.name ?? terminal.organization?.organizationId ?? 'Unassigned';
      if (!grouped[accountKey]) {
        grouped[accountKey] = [];
      }
      grouped[accountKey].push(terminal);
    });
    return grouped;
  }, [filteredTerminals]);

  // Filter out organizations that already have all selected terminals (for "available" destination list)
  const availableOrganizations = useMemo(() => {
    return organizations?.filter(
      organization =>
        !terminalsList.some(t => t.organization?.organizationId === organization.organizationId)
    ) || [];
  }, [organizations, terminalsList]);

  const filteredOrganizations = useMemo(() => {
    return availableOrganizations.filter(
      organization =>
        organization.name?.toLowerCase().includes(organizationSearchTerm.toLowerCase()) ||
        organization.organizationId.toLowerCase().includes(organizationSearchTerm.toLowerCase())
    ) || [];
  }, [availableOrganizations, organizationSearchTerm]);

  const handleTerminalToggle = (terminal: UserTerminal) => {
    setSelectedTerminals(prev =>
      prev.some(t => t.userTerminalId === terminal.userTerminalId)
        ? prev.filter(t => t.userTerminalId !== terminal.userTerminalId)
        : [...prev, terminal]
    );
  };

  const handleAccountGroupToggle = (groupTerminals: UserTerminal[], checked: boolean) => {
    setSelectedTerminals(prev => {
      const groupIds = new Set(groupTerminals.map(t => t.userTerminalId));
      if (checked) {
        const toAdd = groupTerminals.filter(t => !prev.some(p => p.userTerminalId === t.userTerminalId));
        return [...prev, ...toAdd];
      }
      return prev.filter(t => !groupIds.has(t.userTerminalId));
    });
  };

  const isAccountGroupFullySelected = (groupTerminals: UserTerminal[]) =>
    groupTerminals.every(t => selectedTerminals.some(s => s.userTerminalId === t.userTerminalId));

  const isAccountGroupIndeterminate = (groupTerminals: UserTerminal[]) => {
    const selected = groupTerminals.filter(t => selectedTerminals.some(s => s.userTerminalId === t.userTerminalId));
    return selected.length > 0 && selected.length < groupTerminals.length;
  };

  const handleSubmit = async () => {
    if (selectedTerminals.length === 0) {
      showError(ERROR.TERMINAL.SELECT_TERMINALS);
      return;
    }
    if (!selectedOrganizationId) {
      showError(ERROR.TERMINAL.SELECT_ACCOUNT);
      return;
    }

    try {
      await Promise.all(
        selectedTerminals.map(terminal =>
          addTerminalToOrganization.mutateAsync({
            organizationId: selectedOrganizationId,
            terminalId: terminal.userTerminalId,
          })
        )
      );
      showSuccess(SUCCESS.TERMINAL.TRANSFERRED_TO_ACCOUNT);
      onSuccess();
      onOpenChange(false);
      setSelectedOrganizationId('');
      setOrganizationSearchTerm('');
      setTerminalSearchTerm('');
      setSelectedTerminals([]);
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.TRANSFER_TO_ACCOUNT);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setOrganizationSearchTerm('');
      setTerminalSearchTerm('');
      setSelectedOrganizationId('');
      setSelectedTerminals([]);
    }
    onOpenChange(open);
  };

  const canSubmit =
    !addTerminalToOrganization.isPending &&
    selectedTerminals.length > 0 &&
    !!selectedOrganizationId &&
    availableOrganizations.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Transfer Terminal(s) to Organization</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-hidden min-h-0">
          {/* Section 1: Select terminals */}
          <div className="space-y-2">
            <h4 className="font-medium">Select terminals to transfer</h4>
            <SearchBar value={terminalSearchTerm} onChange={setTerminalSearchTerm} placeholder="Search by serial, ID, or account..." />
            {filteredTerminals.length === 0 ? (
              <EmptyView
                icon={SatelliteDish}
                size="small"
                title={terminalSearchTerm ? "No terminals match" : "No terminals"}
                description={terminalSearchTerm ? `No terminals match "${terminalSearchTerm}". Try a different search.` : "There are no terminals available to transfer."}
              />
            ) : (
              <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
                {Object.entries(groupedTerminals).map(([accountKey, groupTerminals]) => (
                  <div key={accountKey} className="space-y-1">
                    <div className="flex items-center gap-2 sticky top-0 bg-background py-1">
                      <Checkbox
                        id={`account-group-${accountKey}`}
                        checked={
                          isAccountGroupFullySelected(groupTerminals)
                            ? true
                            : isAccountGroupIndeterminate(groupTerminals)
                              ? ('indeterminate' as const)
                              : false
                        }
                        onCheckedChange={checked =>
                          handleAccountGroupToggle(groupTerminals, checked === true)
                        }
                      />
                      <Label
                        htmlFor={`account-group-${accountKey}`}
                        className="text-sm font-medium font-bold cursor-pointer"
                      >
                        {accountKey} ({groupTerminals.length})
                      </Label>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pl-6 space-y-1">
                      {groupTerminals.map(terminal => (
                        <div
                          key={terminal.userTerminalId}
                          className="flex items-center gap-2 py-0.5"
                        >
                          <Checkbox
                            id={terminal.userTerminalId}
                            checked={selectedTerminals.some(
                              t => t.userTerminalId === terminal.userTerminalId
                            )}
                            onCheckedChange={() => handleTerminalToggle(terminal)}
                          />
                          <Label
                            htmlFor={terminal.userTerminalId}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            <div className="flex flex-col">
                              <span>
                                {terminal.kitSerialNumber} – {terminal.dishSerialNumber}
                              </span>
                              <span className="text-xs">
                                ID: {terminal.userTerminalId}
                              </span>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedTerminals.length > 0 && (
              <p className="text-xs">
                {selectedTerminals.length} terminal(s) selected
              </p>
            )}
          </div>

          {/* Section 2: Select destination account */}
          <div className="space-y-2 border-t pt-4">
            <h4 className="font-medium">Select destination account</h4>
            {availableOrganizations.length > 0 && (
              <div className="relative">
                <SearchBar value={organizationSearchTerm} onChange={(value) => setOrganizationSearchTerm(value)} placeholder="Search organizations..." />
              </div>
            )}
            {isLoadingOrganizations ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : filteredOrganizations.length === 0 ? (
              organizationSearchTerm ? (
                <EmptyView
                  icon={Search}
                  size="small"
                  title="No accounts found"
                  description={`No accounts match "${organizationSearchTerm}". Try adjusting your search.`}
                />
              ) : (
                <EmptyView
                  icon={User}
                  size="small"
                  title="No accounts available"
                  description="There are no accounts available to transfer terminals to."
                />
              )
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                <RadioGroup
                  value={selectedOrganizationId}
                  onValueChange={setSelectedOrganizationId}
                  className="space-y-2"
                >
                  {filteredOrganizations.map(organization => (
                    <div
                      key={organization.organizationId}
                      className="flex items-center space-x-2"
                    >
                      <RadioGroupItem
                        value={organization.organizationId}
                        id={`account-${organization.organizationId}`}
                      />
                      <Label
                        htmlFor={`account-${organization.organizationId}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        <div className="flex flex-col">
                          <span>{organization.name}</span>
                          <span className="text-xs">
                            ID: {organization.organizationId}
                          </span>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => handleDialogClose(false)}
            disabled={addTerminalToOrganization.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="gap-2"
          >
            {addTerminalToOrganization.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Transferring...
              </>
            ) : (
              'Transfer to selected account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}